import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../kit/Button';
import { haptic } from '../../lib/haptic';

type Props = {
  /** Start odometer, when captured — used as a client-side floor hint. */
  startKm: number | null;
  submitting?: boolean;
  onSave: (data: { releaseKm: number; releasedLocation: string }) => void;
};

/**
 * Post-signature close-out (2026-07-02 lifecycle): after the passenger signs
 * and leaves, the driver records the odometer + location where they were
 * released. KM is REQUIRED — the signature screen no longer shows or captures
 * any readings, so this is the only capture point and billing depends on it.
 * Location stays optional (ops can reconstruct it).
 */
export function ReleaseReadingCard({ startKm, submitting, onSave }: Props) {
  const { t } = useTranslation('trip');
  const [km, setKm] = useState('');
  const [location, setLocation] = useState('');
  const kmValid = /^\d{3,6}$/.test(km);
  const kmOrderingError = kmValid && startKm != null && parseInt(km, 10) <= startKm;
  const isValid = kmValid && !kmOrderingError;

  const inputClass =
    'w-full px-3 py-3 text-xl text-center font-mono bg-[var(--color-surface-soft)] border-2 border-[var(--color-border,#d1d5db)] rounded-2xl focus:border-[var(--green-700,#1B6A41)] outline-none';

  return (
    <div
      className="mx-6 mb-4 p-4 rounded-[var(--radius-card,18px)]"
      style={{
        background: 'var(--color-surface)',
        border: '1.5px solid var(--color-primary)',
        boxShadow: 'var(--shadow-card)',
      }}
      data-testid="release-reading-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          aria-hidden
          style={{
            width: 22, height: 22, borderRadius: 999, background: 'var(--mint-soft)',
            color: 'var(--green-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h2 className="text-base font-extrabold tracking-tight text-[var(--color-text)]">
          {t('customerReleased', { defaultValue: 'Customer released' })}
        </h2>
      </div>
      <p className="text-sm text-[var(--color-text-secondary)] mb-4">
        {t('closeoutHint', { defaultValue: 'Now record the odometer where you were released. This completes your duty entry.' })}
      </p>

      <label className="block mb-3">
        <span className="block text-sm font-medium text-[var(--color-text)] mb-2">
          {t('release_km_label', { defaultValue: 'Release KM' })}
          <span aria-hidden style={{ color: 'var(--color-danger,#C13834)' }}> *</span>
        </span>
        <input
          type="number"
          inputMode="numeric"
          value={km}
          onChange={(e) => setKm(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          className={`${inputClass} tracking-widest [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
        />
        {kmOrderingError ? (
          <p className="mt-1.5 text-xs text-[var(--color-danger,#C13834)]">
            {t('release_km_too_low', { defaultValue: 'Must be more than the start odometer' })} ({startKm?.toLocaleString('en-IN')} km).
          </p>
        ) : startKm != null ? (
          <p className="mt-1.5 text-xs text-[var(--color-text-secondary)]">
            {t('release_km_hint', { defaultValue: 'Start odometer' })}: {startKm.toLocaleString('en-IN')} km
          </p>
        ) : null}
      </label>

      <label className="block mb-4">
        <span className="block text-sm font-medium text-[var(--color-text)] mb-2">
          {t('released_location_label', { defaultValue: 'Released Location' })}
        </span>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={t('released_location_placeholder', { defaultValue: 'e.g. Mumbai Airport T2' })}
          maxLength={500}
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          className={`${inputClass} text-base font-sans text-left`}
        />
      </label>

      <Button
        onClick={() => { haptic.success(); onSave({ releaseKm: parseInt(km, 10), releasedLocation: location.trim() }); }}
        disabled={!isValid}
        loading={submitting}
        className="w-full"
      >
        {t('saveCloseout', { defaultValue: 'Save & finish duty entry' })}
      </Button>
    </div>
  );
}
